import { generateTitle } from 'utils/charts/common';
import { RM_REVENUE_VS_PORTFOLIO_PNL_PORTFOLIO_LEVEL_TITLE } from 'utils/charts/constants';
import BlueCircle from 'assets/images/light-blue-circle-icon.svg';
import {
    axisLabelFormatter,
    getHighestAndInterval,
    tooltipForPortfolioLevel
} from './helpers';

const portfolioGraphic = (containerWidth) => {
    return {
        image: {
            type: 'image',
            $action: 'replace',
            style: {
                image: BlueCircle
            },
            cursor: 'default',
            top: 5.5,
            right: 0.13 * containerWidth - 10,
            height: '8px',
            width: '6px'
        },
        text: {
            type: 'text',
            $action: 'replace',
            style: {
                text: 'Portfolio',
                fontSize: '10px',
                fontWeight: 400,
                font: 'Outfit',
                fill: '#929292'
            },
            cursor: 'default',
            top: 5,
            right: 0.13 * containerWidth - 60,
            silent: true
        }
    };
};

const defaultViewFullViewGraphic = (
    isPortfolioLevelChartInFullView,
    isPortfolioLevelChartInManualZoom
) => {
    const textToShow = isPortfolioLevelChartInManualZoom
        ? ''
        : isPortfolioLevelChartInFullView
        ? 'Full View'
        : 'Default View';
    return {
        text: {
            type: 'text',
            style: {
                text: textToShow,
                font: '10px "Outfit" 400',
                fill: '#929292'
            },
            top: 22,
            left: '1%',
            silent: true,
            $action: 'replace'
        }
    };
};

export const optionsWhenClientIsSelected = (
    containerOffsetWidth,
    containerOffsetHeight,
    clientSelected,
    portfolios,
    outlierData,
    isPortfolioLevelChartInManualZoom,
    isPortfolioLevelChartInFullView
) => {
    const { outliers } = outlierData;
    const outlier =
        outliers && outliers.length > 0
            ? outliers.find((o) => o.rmEmployeeId === clientSelected)
            : [];
    const highestNetDelta =
        portfolios &&
        portfolios.length > 0 &&
        Math.max(
            ...portfolios.reduce(
                (acc, curr) => [...acc, Math.abs(curr.netDelta)],
                []
            )
        );
    const netDeltaMaxAndInterval = getHighestAndInterval(highestNetDelta, 5);

    const highestRevenue =
        portfolios &&
        portfolios.length > 0 &&
        Math.max(
            ...portfolios.reduce(
                (acc, curr) => [...acc, Math.abs(curr.revenue)],
                []
            )
        );
    const revenueMaxAndInterval = getHighestAndInterval(highestRevenue, 10);

    return {
        title: generateTitle(
            `${RM_REVENUE_VS_PORTFOLIO_PNL_PORTFOLIO_LEVEL_TITLE} - ${clientSelected}`
        ),
        grid: {
            top: '23%',
            left: '25px',
            width: '450px',
            height: '65%',
            containLabel: true
        },
        tooltip: tooltipForPortfolioLevel(portfolios, outlier),
        xAxis: {
            type: 'value',
            scale: true,
            name: 'Portfolio Unrealized PnL ($)',
            nameLocation: 'center',
            offset: -13,
            nameGap: 60,
            nameTextStyle: {
                color: '#7FADBF',
                fontWeight: 600,
                fontSize: 12,
                width: '100%',
                verticalAlign: 'middle'
            },
            axisLabel: {
                formatter: function (value) {
                    return axisLabelFormatter(
                        value,
                        netDeltaMaxAndInterval.digit,
                        true
                    );
                },
                color: '#929292',
                fontSize: 10,
                fontWeight: 400
            },
            axisLine: {
                show: false
            },
            axisTick: {
                show: false,
                length: 3
            },
            splitLine: {
                show: false
            },
            min: -netDeltaMaxAndInterval.roundedHighest,
            max: netDeltaMaxAndInterval.roundedHighest,
            interval: netDeltaMaxAndInterval.interval
        },
        yAxis: {
            type: 'value',
            name: 'RM Revenue ($)',
            nameLocation: 'center',
            offset: -227,
            nameGap: isPortfolioLevelChartInFullView ? 270 : 272,
            nameTextStyle: {
                color: '#7FADBF',
                fontWeight: 600,
                fontSize: 12,
                width: '100%',
                verticalAlign: 'middle'
            },
            axisLabel: {
                formatter: function (value) {
                    return axisLabelFormatter(
                        value,
                        revenueMaxAndInterval.digit,
                        false
                    );
                },
                color: '#929292',
                fontSize: 10,
                fontWeight: 400
            },
            axisLine: {
                show: false
            },
            axisTick: {
                show: false
            },
            splitLine: {
                show: false
            },
            min: 0,
            max: revenueMaxAndInterval.roundedHighest,
            interval: revenueMaxAndInterval.interval,
            onZero: true
        },
        dataZoom: [
            {
                type: 'inside',
                xAxisIndex: [0],
                startValue: -netDeltaMaxAndInterval.roundedHighest,
                endValue: netDeltaMaxAndInterval.roundedHighest
            },
            {
                type: 'inside',
                yAxisIndex: [0],
                startValue: 0,
                endValue: revenueMaxAndInterval.roundedHighest
            }
        ],
        series: [
            {
                type: 'scatter',
                data:
                    portfolios && portfolios.length > 0
                        ? portfolios.map((rm) => {
                              return {
                                  name: rm.portfolioId,
                                  value: [rm.netDelta, rm.revenue]
                              };
                          })
                        : [],
                itemStyle: {
                    color: '#9BCEE2'
                },
                symbolSize: 12
            }
        ],
        graphic: [
            portfolioGraphic(containerOffsetWidth).image,
            portfolioGraphic(containerOffsetWidth).text,
            defaultViewFullViewGraphic(
                isPortfolioLevelChartInFullView,
                isPortfolioLevelChartInManualZoom
            ).text
        ]
    };
};

export const optionsWhenClientIsNotSelected = () => {
    return {
        title: generateTitle(RM_REVENUE_VS_PORTFOLIO_PNL_PORTFOLIO_LEVEL_TITLE),
        xAxis: { axisLine: false },
        yAxis: { axisLine: false },
        graphic: [
            {
                type: 'text',
                $action: 'replace',
                style: {
                    text: 'Select a RM from',
                    font: 'normal 600 12px "Outfit", sans-serif',
                    fill: '#7FADBF'
                },
                cursor: 'default',
                top: '40%',
                left: '40%'
            },
            {
                type: 'text',
                $action: 'replace',
                style: {
                    text: 'RM Revenue vs Portfolio Unrealized PnL - RM level Chart',
                    font: 'normal 600 12px "Outfit", sans-serif',
                    fill: '#7FADBF'
                },
                cursor: 'default',
                top: '44%',
                left: '20%'
            }
        ]
    };
};
